import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Separator } from "../components/ui/separator";
import {
  Car, ArrowLeft, ArrowRight, MapPin, Globe, DollarSign, FileText,
  CheckCircle2, AlertTriangle, Loader2, BookOpen, Languages,
} from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DMVGuidePage() {
  const { stateCode } = useParams();
  const [states, setStates] = useState([]);
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        if (stateCode) {
          const res = await axios.get(`${API}/dmv/states/${stateCode}`);
          setState(res.data);
        } else {
          const res = await axios.get(`${API}/dmv/states`);
          setStates(res.data);
        }
      } catch { /* ignore */ }
      setLoading(false);
    };
    load();
  }, [stateCode]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="dmv-loading">
        <Loader2 className="h-8 w-8 text-teal-600 animate-spin" />
      </div>
    );
  }

  // State list
  if (!stateCode) {
    return (
      <main className="min-h-screen bg-background" data-testid="dmv-states-list">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-12 md:py-20">
          <div className="text-center mb-16">
            <Badge variant="secondary" className="bg-sky-100 text-sky-800 hover:bg-sky-100 mb-4 font-medium">Driver License for Immigrants</Badge>
            <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 font-heading mb-4">
              Driver's License Guide by State
            </h1>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Everything immigrants need to know about getting a driver's license in each U.S. state.
              Requirements, tests, fees, and tips specific to your state.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {states.map((s) => (
              <Link to={`/dmv-guide/${s.state_code}`} key={s.state_code}>
                <Card className="border-slate-100 hover:border-sky-200 hover:shadow-md transition-all duration-300 card-hover" data-testid={`dmv-state-${s.state_code}`}>
                  <CardContent className="p-5">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="h-10 w-10 rounded-lg bg-sky-50 flex items-center justify-center">
                        <Car className="h-5 w-5 text-sky-600" />
                      </div>
                      <div>
                        <p className="text-lg font-bold text-slate-900">{s.state_code}</p>
                        <p className="text-xs text-slate-500">{s.state_name}</p>
                      </div>
                    </div>
                    <p className="text-xs text-slate-500">{s.office_name}</p>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        </div>
      </main>
    );
  }

  // State detail
  if (!state) return null;

  return (
    <main className="min-h-screen bg-background" data-testid="dmv-state-detail">
      <div className="max-w-4xl mx-auto px-4 md:px-6 py-8 md:py-12">
        <Link to="/dmv-guide">
          <Button variant="ghost" className="mb-6 text-slate-600" data-testid="back-to-states">
            <ArrowLeft className="mr-2 h-4 w-4" /> All States
          </Button>
        </Link>

        {/* Header */}
        <div className="mb-10">
          <div className="flex items-center gap-4 mb-4">
            <div className="h-14 w-14 rounded-xl bg-sky-50 flex items-center justify-center">
              <Car className="h-7 w-7 text-sky-600" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-slate-900 font-heading">
                {state.state_name} Driver's License
              </h1>
              <p className="text-sm text-slate-500">{state.office_name}</p>
            </div>
          </div>
          <p className="text-base text-slate-600 mb-4">{state.overview}</p>
          <a href={state.website} target="_blank" rel="noopener noreferrer">
            <Button variant="outline" size="sm" className="text-sky-700 border-sky-200" data-testid="dmv-website-link">
              <Globe className="mr-2 h-3.5 w-3.5" /> Official Website
            </Button>
          </a>
        </div>

        <Separator className="mb-8" />

        {/* Documents needed */}
        <section className="mb-10">
          <h2 className="text-xl font-semibold text-slate-900 font-heading mb-4">Required Documents</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {Object.entries(state.documents_needed || {}).map(([cat, docs]) => (
              <Card key={cat} className="border-slate-100" data-testid={`dmv-docs-${cat}`}>
                <CardContent className="p-5">
                  <h3 className="text-sm font-semibold text-slate-900 capitalize mb-3">{cat.replace("_", " ")}</h3>
                  <ul className="space-y-2">
                    {docs.map((d, i) => (
                      <li key={i} className="text-sm text-slate-600 flex items-start gap-2">
                        <FileText className="h-3.5 w-3.5 text-slate-400 mt-0.5 flex-shrink-0" /> {d}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>

        {/* Written test */}
        <section className="mb-10">
          <h2 className="text-xl font-semibold text-slate-900 font-heading mb-4">Written Knowledge Test</h2>
          <Card className="border-slate-100">
            <CardContent className="p-6">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                <div>
                  <p className="text-xs text-slate-500 mb-1">Questions</p>
                  <p className="text-lg font-bold text-slate-900">{state.written_test?.questions}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 mb-1">Passing Score</p>
                  <p className="text-lg font-bold text-slate-900">{state.written_test?.passing_score}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 mb-1">Attempts</p>
                  <p className="text-sm font-medium text-slate-700">{state.written_test?.attempts}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 mb-1">Languages</p>
                  <p className="text-sm font-medium text-slate-700">{state.written_test?.languages?.length}+ languages</p>
                </div>
              </div>
              <div className="flex flex-wrap gap-1.5 mb-4">
                {state.written_test?.languages?.map((l) => (
                  <Badge key={l} variant="outline" className="text-xs">{l}</Badge>
                ))}
              </div>
              {state.written_test?.tips?.length > 0 && (
                <div className="bg-teal-50 border border-teal-200 rounded-lg p-4">
                  <p className="text-sm font-medium text-teal-800 mb-2">Study Tips:</p>
                  <ul className="space-y-1.5">
                    {state.written_test.tips.map((t, i) => (
                      <li key={i} className="text-sm text-teal-700 flex items-start gap-2">
                        <CheckCircle2 className="h-3.5 w-3.5 text-teal-500 mt-0.5 flex-shrink-0" /> {t}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </CardContent>
          </Card>
        </section>

        {/* Driving test */}
        <section className="mb-10">
          <h2 className="text-xl font-semibold text-slate-900 font-heading mb-4">Behind-the-Wheel Test</h2>
          <Card className="border-slate-100">
            <CardContent className="p-6">
              <p className="text-sm text-slate-600 mb-4">Duration: <strong>{state.driving_test?.duration}</strong></p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <p className="text-sm font-medium text-slate-800 mb-2">Requirements:</p>
                  <ul className="space-y-1.5">
                    {state.driving_test?.requirements?.map((r, i) => (
                      <li key={i} className="text-sm text-slate-600 flex items-start gap-2">
                        <FileText className="h-3.5 w-3.5 text-slate-400 mt-0.5 flex-shrink-0" /> {r}
                      </li>
                    ))}
                  </ul>
                </div>
                <div>
                  <p className="text-sm font-medium text-slate-800 mb-2">What They Test:</p>
                  <ul className="space-y-1.5">
                    {state.driving_test?.what_they_test?.map((w, i) => (
                      <li key={i} className="text-sm text-slate-600 flex items-start gap-2">
                        <CheckCircle2 className="h-3.5 w-3.5 text-teal-500 mt-0.5 flex-shrink-0" /> {w}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>
        </section>

        {/* Fees */}
        <section className="mb-10">
          <h2 className="text-xl font-semibold text-slate-900 font-heading mb-4">Fees</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {Object.entries(state.fees || {}).map(([key, val]) => (
              <Card key={key} className="border-slate-100">
                <CardContent className="p-4 text-center">
                  <DollarSign className="h-4 w-4 text-slate-400 mx-auto mb-1" />
                  <p className="text-lg font-bold text-slate-900">{val}</p>
                  <p className="text-xs text-slate-500 capitalize">{key.replace(/_/g, " ")}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>

        {/* Tips for immigrants */}
        {state.tips_for_immigrants?.length > 0 && (
          <section className="mb-10">
            <h2 className="text-xl font-semibold text-slate-900 font-heading mb-4">Tips for Immigrants</h2>
            <Card className="border-teal-200 bg-teal-50/30">
              <CardContent className="p-6">
                <div className="space-y-3">
                  {state.tips_for_immigrants.map((tip, i) => (
                    <div key={i} className="flex items-start gap-3" data-testid={`dmv-tip-${i}`}>
                      <CheckCircle2 className="h-4 w-4 text-teal-600 mt-0.5 flex-shrink-0" />
                      <p className="text-sm text-slate-700">{tip}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </section>
        )}

        {/* CTA */}
        <section className="bg-sky-700 rounded-2xl p-8 text-center" data-testid="dmv-cta">
          <Car className="h-10 w-10 text-sky-200 mx-auto mb-4" />
          <h2 className="text-2xl font-semibold text-white mb-3 font-heading">
            Ready to get your {state.state_name} license?
          </h2>
          <p className="text-sky-100 mb-6">Let ImmigrationGuide help you prepare all your documents and forms.</p>
          <Link to="/auth?mode=register">
            <Button size="lg" className="bg-white text-sky-700 hover:bg-sky-50 font-medium px-8" data-testid="dmv-start-btn">
              Start with AI Assistant <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
        </section>
      </div>
    </main>
  );
}
